import React, { useState, useEffect, useRef } from 'react';
import { Settings, Save, Upload, Trash2, RefreshCw } from 'lucide-react';
import { getProjectData, saveProjectData, getGlobalData, saveGlobalData } from '../utils/projectStorage';
import './Settings.css';

interface CaseStudiesSettings {
  generalInstructions: string;
  caseStudyTemplate: string;
}

const CaseStudiesSettings: React.FC = () => {
  const [settings, setSettings] = useState<CaseStudiesSettings>({
    generalInstructions: `I am going to give you some content for a case study. What I want is for you to craft different case studies, based on one of the 8 dimensions of motivation.

I will tell you the dimension of the recipient. If I don't, make sure to ask me for it.

Most case studies are often written for Achievers or Drivers. But I want you to write one for anyone, based on their dimension.

Each case study should use the same core facts and quotes from the original story but frame them through a different motivational lens, ensuring that readers with different motivational wiring will connect with the version that speaks to their primary drivers and values.

Achiever: Focuses on excellence, recognition, and standout performance

Driver: Emphasizes overcoming obstacles, determination, and pushing forward Influencer: Highlights potential, transformation, and inspiring others

Learner: Centers on knowledge, research, and systematic understanding

Optimizer: Concentrates on efficiency, process improvement, and precision Orchestrator: Features coordination, strategy, and bringing elements together

Relator: Emphasizes teamwork, connection, and collective success

Visionary: Focuses on innovation, creativity, and transforming possibilities`,
    caseStudyTemplate: `# Case Study: [Title]

## Background
- **Company/Organization**: [Name and brief description]
- **Industry**: [Industry type]
- **Challenge**: [Primary challenge or goal]
- **Timeline**: [Implementation timeframe]

## Motivational Dimension Focus
**Primary Dimension**: [Selected dimension]
- [Key characteristics of this dimension]
- [How this dimension influences behavior and decisions]

## Solution Approach
- [Strategy implemented]
- [Specific tactics used] 
- [How the approach was tailored to the motivational dimension]

## Implementation
- [Step-by-step process]
- [Key stakeholders involved]
- [Resources utilized]

## Results
- [Quantifiable outcomes]
- [Qualitative improvements]
- [Impact on team/organization]

## Key Learnings
- [Primary insights gained]
- [Best practices identified]
- [Recommendations for similar implementations]

## Dimension-Specific Insights
- [How the motivational dimension influenced the success]
- [Specific strategies that resonated with this dimension]
- [Adaptations made based on dimensional characteristics]`
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Load existing settings
    const projectData = getProjectData('case-studies');
    if (projectData.settings) {
      setSettings(projectData.settings);
    }
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus('Saving settings...');
    
    try {
      // Get motivational dimensions content from global data
      const globalData = getGlobalData();
      const motivationalDimensionsContent = globalData.motivationalDimensionsFile?.content || '';
      
      // Create the ingested content that combines settings with motivational dimensions
      const ingestedContent = `${settings.generalInstructions || 'Create comprehensive case studies that demonstrate successful implementation of motivational dimension-based strategies.'}

## Case Study Template
${settings.caseStudyTemplate}

${motivationalDimensionsContent ? `## Motivational Dimensions Framework
The following is the complete MCode Motivational Dimensions framework that you must use when creating case studies:

${motivationalDimensionsContent}

Please ensure you fully understand and apply these motivational dimensions when crafting case studies tailored to specific personality types.` : ''}

When creating case studies, always:
1. Use the provided template as a structure guide
2. Tailor the content specifically to the selected motivational dimension
3. Include concrete examples and actionable insights
4. Focus on practical applications of the MCode framework
5. Provide quantifiable results where possible
6. Ensure the case study demonstrates clear value proposition

Do not include any dimensions or motivations that are not in the lists above. Please be very precise with the spelling and capitalization of dimension and motivation names.`;

      // Save settings and generated content to project data
      const projectData = getProjectData('case-studies');
      projectData.settings = settings;
      projectData.ingestedContent = ingestedContent;
      
      saveProjectData('case-studies', projectData);
      
      setSaveStatus('Settings saved successfully!');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveStatus('Error saving settings');
      setTimeout(() => setSaveStatus(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleMotivationalDimensionsUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const content = await file.text();
        const globalData = getGlobalData();
        globalData.motivationalDimensionsFile = {
          name: file.name,
          content,
          lastModified: file.lastModified,
          size: file.size
        };
        saveGlobalData(globalData);
        
        // Auto-save settings to update ingested content with new file
        await handleSave();
      } catch (error) {
        console.error('Error reading motivational dimensions file:', error);
      }
    }
  };

  const removeFile = async () => {
    const globalData = getGlobalData();
    globalData.motivationalDimensionsFile = undefined;
    saveGlobalData(globalData);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    // Auto-save settings to update ingested content
    await handleSave();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="settings-page">
      <h2>Case Studies Settings</h2>

      {/* General Instructions */}
      <section className="settings-section">
        <h3><Settings size={20} /> General Instructions</h3>
        <div className="prompt-section">
          <h4>General Instructions Prompt</h4>
          <textarea
            value={settings.generalInstructions}
            onChange={(e) => setSettings({ ...settings, generalInstructions: e.target.value })}
            className="prompt-textarea"
            rows={4}
            placeholder="Enter general instructions for case study creation..."
          />
          <p className="field-description">
            Provide overall guidance for how case studies should be created and structured.
          </p>
        </div>
      </section>

      {/* Case Study Template */}
      <section className="settings-section">
        <h3>Case Study Template</h3>
        <div className="prompt-section">
          <h4>Template Structure</h4>
          <textarea
            value={settings.caseStudyTemplate}
            onChange={(e) => setSettings({ ...settings, caseStudyTemplate: e.target.value })}
            className="prompt-textarea"
            rows={12}
            placeholder="Enter the case study template structure..."
          />
          <p className="field-description">
            Define the standard template structure that should be used for all case studies.
          </p>
        </div>
      </section>

      {/* Motivational Dimensions File */}
      <section className="settings-section">
        <h3>MCode Motivational Dimensions</h3>
        
        {(() => {
          const globalData = getGlobalData();
          return globalData.motivationalDimensionsFile ? (
            <div className="file-display">
              <div className="file-info">
                <Upload size={20} />
                <div className="file-details">
                  <span className="file-name">{globalData.motivationalDimensionsFile.name}</span>
                  <span className="file-meta">
                    {formatFileSize(globalData.motivationalDimensionsFile.size)} • 
                    {new Date(globalData.motivationalDimensionsFile.lastModified).toLocaleDateString()}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={removeFile}
                  className="remove-file-btn"
                  title="Remove file"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="file-content-preview">
                <strong>File Content Preview:</strong>
                <div className="content-preview">
                  {globalData.motivationalDimensionsFile.content.substring(0, 200)}
                  {globalData.motivationalDimensionsFile.content.length > 200 && '...'}
                </div>
              </div>
            </div>
          ) : (
            <div className="file-upload">
              <input
                ref={fileInputRef}
                type="file"
                id="mcodeDimensionsFile"
                accept=".txt,.md,.json"
                onChange={handleMotivationalDimensionsUpload}
                className="file-input"
              />
              <label htmlFor="mcodeDimensionsFile" className="file-upload-label">
                <Upload size={20} />
                <span>Upload MCode Dimensions File</span>
                <span className="file-types">Supported: .txt, .md, .json (max 5MB)</span>
              </label>
            </div>
          );
        })()}
        
        <p className="field-description">
          Upload a file containing MCODE dimensional definitions. This file will be shared across all projects.
        </p>
      </section>

      {/* Save Settings */}
      <section className="settings-section">
        <h3><RefreshCw size={20} /> Save Configuration</h3>
        <button 
          onClick={handleSave}
          className="process-btn"
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <RefreshCw className="spinner" size={16} />
              Saving...
            </>
          ) : (
            <>
              <Save size={16} />
              Save Settings
            </>
          )}
        </button>
        {saveStatus && (
          <div className="process-status">
            {saveStatus}
          </div>
        )}
      </section>
    </div>
  );
};

export default CaseStudiesSettings;